/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * A service for reading and manipulating the set of connections that the
 * current user has marked as favorites (pinned). Favorites are persisted
 * server-side, scoped to the current user's account, and are retrieved from
 * every data source available to the current user.
 */
angular.module('home').factory('guacFavorites', ['$injector',
        function guacFavorites($injector) {

    // Required types
    var ClientIdentifier = $injector.get('ClientIdentifier');

    // Required services
    var authenticationService     = $injector.get('authenticationService');
    var dataSourceService         = $injector.get('dataSourceService');
    var favoriteConnectionService = $injector.get('favoriteConnectionService');
    var requestService            = $injector.get('requestService');

    var service = {};

    /**
     * The client identifiers of all connections that the current user has
     * marked as favorites, in no particular order.
     *
     * @type String[]
     */
    service.favorites = [];

    /**
     * Whether the initial retrieval of favorite connections from the REST
     * API has completed.
     *
     * @type Boolean
     */
    var loaded = false;

    /**
     * Returns whether the initial retrieval of favorite connections from the
     * REST API has completed.
     *
     * @returns {Boolean}
     *     true if the initial retrieval of favorite connections has
     *     completed, false otherwise.
     */
    service.isLoaded = function isLoaded() {
        return loaded;
    };

    /**
     * Returns whether the connection having the given client identifier has
     * been marked as a favorite.
     *
     * @param {String} id
     *     The client identifier of the connection to check.
     *
     * @returns {Boolean}
     *     true if the given identifier is currently marked as a favorite,
     *     false otherwise.
     */
    service.isFavorite = function isFavorite(id) {
        return service.favorites.indexOf(id) !== -1;
    };

    /**
     * Marks the connection having the given client identifier as a
     * favorite. If the identifier is already marked as a favorite, this
     * function has no effect. The change is optimistically reflected
     * locally and persisted via the REST API; if the REST API request
     * fails, the local change is rolled back.
     *
     * @param {String} id
     *     The client identifier of the connection to mark as a favorite.
     */
    service.addFavorite = function addFavorite(id) {

        if (service.isFavorite(id))
            return;

        var identifier = ClientIdentifier.fromString(id);
        service.favorites.push(id);

        favoriteConnectionService.patchFavoriteConnections(
            identifier.dataSource, [identifier.id], [])
        .catch(function addFavoriteFailed() {
            var index = service.favorites.indexOf(id);
            if (index !== -1)
                service.favorites.splice(index, 1);
        });

    };

    /**
     * Removes the connection having the given client identifier from the
     * set of favorites. If the identifier is not currently marked as a
     * favorite, this function has no effect. The change is optimistically
     * reflected locally and persisted via the REST API; if the REST API
     * request fails, the local change is rolled back.
     *
     * @param {String} id
     *     The client identifier of the connection to remove from the set of
     *     favorites.
     */
    service.removeFavorite = function removeFavorite(id) {

        var index = service.favorites.indexOf(id);
        if (index === -1)
            return;

        var identifier = ClientIdentifier.fromString(id);
        service.favorites.splice(index, 1);

        favoriteConnectionService.patchFavoriteConnections(
            identifier.dataSource, [], [identifier.id])
        .catch(function removeFavoriteFailed() {
            if (service.favorites.indexOf(id) === -1)
                service.favorites.push(id);
        });

    };

    /**
     * Toggles the favorite status of the connection having the given client
     * identifier. If the identifier is currently marked as a favorite it
     * will be unmarked, and vice versa.
     *
     * @param {String} id
     *     The client identifier of the connection whose favorite status
     *     should be toggled.
     */
    service.toggleFavorite = function toggleFavorite(id) {

        if (service.isFavorite(id))
            service.removeFavorite(id);
        else
            service.addFavorite(id);

    };

    // Retrieve the current user's favorite connections from every data
    // source available to them, converting the raw connection identifiers
    // returned by the REST API into the combined ClientIdentifier format
    // used throughout the home screen
    dataSourceService.apply(
        favoriteConnectionService.getFavoriteConnections,
        authenticationService.getAvailableDataSources()
    )
    .then(function favoritesRetrieved(favoritesByDataSource) {

        angular.forEach(favoritesByDataSource, function addFavoritesForDataSource(identifiers, dataSource) {
            angular.forEach(identifiers, function addFavoriteForDataSource(identifier) {
                service.favorites.push(ClientIdentifier.toString({
                    dataSource : dataSource,
                    type       : ClientIdentifier.Types.CONNECTION,
                    id         : identifier
                }));
            });
        });

        loaded = true;

    }, requestService.DIE);

    return service;

}]);
